import { FC, useCallback, useState, memo, useEffect } from 'react'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { UserImage, UserImageId } from 'interfaces/userImage'
import { AvatarEditable } from 'components/features/socialFeed/AvatarEditable/AvatarEdiatble'
import { Avatar } from 'components/features/socialFeed/Avatar/Avatar'
import { ImageId } from 'interfaces/media'
import { userImageSelector, userImageByIdSelector } from 'selectors/userImagesSelectors'
import { shallowEqual } from 'react-redux'
import { userImageUploadActionCreator } from 'actions/userImageActions'
import { change, WrappedFieldProps } from 'redux-form'
import { State } from 'interfaces/state'
import ErrorImageSvg from 'icons/report.svg'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import * as styles from 'components/features/socialFeed/AvatarField/AvatarField.styl'
import SvgIcon from 'components/atoms/SvgIcon'
import * as cn from 'classnames'
import { CropperFileInput } from 'components/features/pfm/CropperFileInput/CropperFileInput'

type AvatarFieldProps =
  & WrappedFieldProps<State>
  & {
    username: string
    onChange: (imageId: ImageId) => void
    inFormMode?: boolean
  }

type AvatarFieldState = {
  imageId: ImageId | null
  localImageId: UserImageId | null
  isError: boolean
}

export const AvatarField: FC<AvatarFieldProps> = memo(({
  username,
  inFormMode,
  meta: {
    form: formName,
  },
  input,
}) => {
  const value: ImageId | null = input ? input.value || null : null

  const [ownState, setOwnState] = useState<AvatarFieldState>({
    imageId: value,
    localImageId: null,
    isError: false,
  })
  const { imageId, localImageId, isError } = ownState

  const avatar = useStoreSelector<UserImage | null>((state) => userImageByIdSelector(state, imageId), shallowEqual)
  const localAvatar = useStoreSelector<UserImage | null>((state) => userImageSelector(state, localImageId), shallowEqual)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    if (localAvatar && localAvatar.image && localAvatar.image.id !== imageId) {
      setOwnState({
        imageId: localAvatar.image.id,
        localImageId: null,
        isError: false,
      })

      // don't use onChange it crushes react
      if (formName && input) {
        dispatch(change(formName, input.name, localAvatar.image.id))
      }
    }
  }, [dispatch, formName, imageId, input, localAvatar])

  const handleFiles = useCallback<(file: File) => void>((file) => {
    const localId = -1 * Math.random()
    dispatch(userImageUploadActionCreator.request({
      file,
      type: 'avatar',
      id: localId,
    }))

    setOwnState({
      imageId: null,
      localImageId: localId,
      isError: false,
    })
  }, [dispatch])

  const handleError = useCallback(() => {
    setOwnState({
      ...ownState,
      isError: true,
    })
  }, [ownState])

  const image = avatar || localAvatar
  const isLoading = !!(image && image.isLoading)
  const isErrorHappens = !!(image && image.errorCode !== null) || isError

  return (
    <CropperFileInput
      onCrop={ handleFiles }
      onError={ handleError }
    >
      <div className={ cn(styles.avatarField, { [styles.formMode]: inFormMode }) }>
        { inFormMode && <div className={ styles.avatarTitle }>Feed Profile Picture</div> }
        <div className={ cn(styles.container, isErrorHappens && styles.error) }>
          <AvatarEditable actionTitle={ image ? 'Edit' : 'Add' }>
            <Avatar
              isOwn
              name={ username }
              avatar={ image }
              matchRatio={ 1 }
              dim
            />
            { isLoading && !isErrorHappens &&
              <div className={ styles.preloader }>
                <Spinner />
              </div>}
            { isErrorHappens &&
              <div className={ styles.errorIcon }>
                <SvgIcon src={ ErrorImageSvg } />
              </div>}
          </AvatarEditable>
        </div>
      </div>
    </CropperFileInput>
  )
})
